import { Box, MenuItem, Paper, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { listAccounts } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import { FlowisAvailabilityBanner } from '../../components/FlowisAvailabilityBanner';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { useTranslation } from 'react-i18next';
import { useNoTenantMessageKey, useSelectedTenant } from '../../tenant/tenantSelection';
import { accountLabel } from '../../utils/format';
import { ReceivedTab } from './ReceivedTab';
import { SentTab } from './SentTab';

export function FlowisPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const accountGid = searchParams.get('account') ?? '';
  const [tab, setTab] = useState<'sent' | 'received'>('sent');
  const { t } = useTranslation();
  const { selectedTenantGid } = useSelectedTenant();
  // Nothing to query: an administrator has not picked a tenant yet, or the caller holds none.
  const hasNoTenant = selectedTenantGid === null;
  const noTenantKey = useNoTenantMessageKey();

  // A tenant-less query reaches the fallback schema and fails instead of returning an empty list.
  const accountsQuery = useQuery({
    queryKey: queryKeys.accounts.list(selectedTenantGid),
    queryFn: ({ signal }) => listAccounts(signal),
    enabled: selectedTenantGid !== null,
  });

  const accounts = accountsQuery.data;

  useEffect(() => {
    if (accountGid || !accounts || accounts.length === 0) return;
    const first = accounts.find((account) => account.enabled) ?? accounts[0];
    if (first) setSearchParams({ account: first.accountGid }, { replace: true });
  }, [accountGid, accounts, setSearchParams]);

  return (
    <Stack spacing={2}>
      <FlowisAvailabilityBanner />

      <Box>
        <Typography variant="h5">{t('flowis.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('flowis.description')}
        </Typography>
      </Box>

      <Paper sx={{ p: 2 }}>
        {!hasNoTenant && accountsQuery.isPending && <LoadingBlock label={t('flowis.loadingAccounts')} />}
        {!hasNoTenant && accountsQuery.isError && (
          <ErrorBlock error={accountsQuery.error} onRetry={() => void accountsQuery.refetch()} />
        )}
        {!hasNoTenant && accounts && (
          <TextField
            select
            size="small"
            label={t('flowis.account')}
            value={accountGid}
            onChange={(event) => setSearchParams({ account: event.target.value })}
            sx={{ minWidth: 260 }}
          >
            {accounts.map((account) => (
              <MenuItem key={account.accountGid} value={account.accountGid} disabled={!account.enabled}>
                {accountLabel(account)}
                {!account.enabled && t('flowis.disabledSuffix')}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Paper>

      {hasNoTenant ? (
        <EmptyBlock message={t(noTenantKey)} />
      ) : (
        accounts && accounts.length === 0 && <EmptyBlock message={t('flowis.noAccounts')} />
      )}

      {!hasNoTenant && accountGid && (
        <>
          <Tabs value={tab} onChange={(_event, value: 'sent' | 'received') => setTab(value)}>
            <Tab value="sent" label={t('flowis.tabs.sent')} />
            <Tab value="received" label={t('flowis.tabs.received')} />
          </Tabs>

          {tab === 'sent' ? (
            <SentTab accountGid={accountGid} />
          ) : (
            // Two identifiers because two APIs: the gate's own lists are addressed by our handle,
            // while pulling a document into storage addresses the account the way a sender does.
            <ReceivedTab
              accountGid={accountGid}
              participantId={accounts?.find((a) => a.accountGid === accountGid)?.participantId ?? null}
            />
          )}
        </>
      )}
    </Stack>
  );
}
